<template>
    <div class="min-h-screen bg-gray-50 flex flex-col" :style="cssVars">

        <!-- ── Loader ────────────────────────────────────────────────── -->
        <div v-if="loadingGetReservation" class="flex-1 flex flex-col items-center justify-center gap-5 px-4">
            <div class="relative w-14 h-14">
                <div class="absolute inset-0 rounded-full border-4 border-gray-200"></div>
                <div
                    class="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin"
                    :style="{ borderColor: 'var(--cp)', borderTopColor: 'transparent' }"
                ></div>
            </div>
            <p class="text-sm text-gray-400 tracking-wide">Chargement de vos créneaux…</p>
        </div>

        <!-- ── Erreur ─────────────────────────────────────────────────── -->
        <div v-else-if="alertMessage" class="flex-1 flex items-center justify-center px-4">
            <div class="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <div class="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                    <i class="fas fa-triangle-exclamation text-red-400 text-xl"></i>
                </div>
                <p class="font-semibold text-gray-900 mb-2">Lien invalide</p>
                <p class="text-sm text-gray-500 leading-relaxed">{{ alertMessage }}</p>
            </div>
        </div>

        <!-- ── Succès ─────────────────────────────────────────────────── -->
        <div v-else-if="confirmed" class="flex-1 flex items-center justify-center px-4">
            <div class="max-w-sm w-full text-center">
                <div
                    class="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                    :style="{ backgroundColor: 'var(--cp-light)' }"
                >
                    <i class="fas fa-circle-check text-4xl" :style="{ color: 'var(--cp)' }"></i>
                </div>

                <template v-if="noneOfTheAbove">
                    <h2 class="text-2xl font-bold text-gray-900 mb-2">Message envoyé !</h2>
                    <p class="text-gray-500 text-sm leading-relaxed mb-6">
                        Vos disponibilités ont bien été transmises à
                        <strong class="text-gray-700">{{ companyName }}</strong>.
                        Nous reviendrons vers vous prochainement pour convenir d'un rendez-vous.
                    </p>
                </template>
                <template v-else>
                    <h2 class="text-2xl font-bold text-gray-900 mb-2">Créneau confirmé !</h2>
                    <p class="text-gray-500 text-sm leading-relaxed mb-6">
                        Votre choix a bien été enregistré.
                        <strong class="text-gray-700">{{ companyName }}</strong> vous adressera
                        une confirmation prochainement.
                    </p>
                    <div
                        class="rounded-xl px-5 py-4 text-left"
                        :style="{ backgroundColor: 'var(--cp-light)', border: '1px solid var(--cp)' }"
                    >
                        <p class="text-xs font-semibold uppercase tracking-widest mb-2" :style="{ color: 'var(--cp)' }">
                            Votre rendez-vous
                        </p>
                        <p class="font-bold text-gray-900 text-base">{{ formatDate(selectedSlot.date) }}</p>
                        <p class="text-gray-600 text-sm mt-0.5">{{ slotLabel(selectedSlot.slot) }}</p>
                    </div>
                </template>
            </div>
        </div>

        <!-- ── Contenu principal ──────────────────────────────────────── -->
        <template v-else-if="reservation">

            <!-- Header -->
            <header class="bg-white border-b border-gray-100 sticky top-0 z-10">
                <div class="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
                    <img
                        v-if="config.branding_logo_url"
                        :src="config.branding_logo_url"
                        class="h-8 w-auto object-contain"
                        :alt="companyName"
                    />
                    <div
                        v-else
                        class="w-8 h-8 rounded-lg flex items-center justify-center"
                        :style="{ backgroundColor: 'var(--cp-light)' }"
                    >
                        <i :class="config.branding_icon || 'fas fa-calendar'" class="text-sm" :style="{ color: 'var(--cp)' }"></i>
                    </div>
                    <span class="font-semibold text-gray-900 text-sm">{{ companyName }}</span>
                </div>
            </header>

            <!-- Body -->
            <main class="flex-1 max-w-xl mx-auto w-full px-4 py-8">

                <!-- Intro -->
                <div class="mb-8">
                    <p class="text-xs font-semibold uppercase tracking-widest mb-2" :style="{ color: 'var(--cp)' }">
                        Prise de rendez-vous
                    </p>
                    <h1 class="text-2xl font-bold text-gray-900 leading-tight mb-2">
                        Choisissez votre créneau
                    </h1>
                    <p class="text-gray-500 text-sm leading-relaxed">
                        Bonjour <strong class="text-gray-700">{{ reservation.client_prenom }} {{ reservation.client_nom }}</strong>,
                        sélectionnez le créneau qui vous convient parmi les disponibilités proposées.
                    </p>
                </div>

                <!-- Badges info -->
                <div class="flex items-center flex-wrap gap-2 mb-6">
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-3 py-1.5">
                        <i class="fas fa-wrench text-xs"></i>
                        {{ appareilLabel }}
                    </span>
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-3 py-1.5">
                        <i class="fas fa-location-dot text-xs"></i>
                        {{ reservation.ville }}
                    </span>
                </div>

                <!-- Créneaux -->
                <div class="space-y-3 mb-3">
                    <button
                        v-for="(prop, index) in propositions"
                        :key="index"
                        @click="selectedSlot = prop; noneOfTheAbove = false"
                        class="w-full text-left rounded-2xl border-2 transition-all duration-150 overflow-hidden"
                        :class="isSelected(prop) ? 'shadow-md' : 'border-gray-150 bg-white hover:border-gray-300 shadow-sm hover:shadow'"
                        :style="isSelected(prop) ? { borderColor: 'var(--cp)', backgroundColor: 'var(--cp-light)' } : {}"
                    >
                        <div class="flex items-center gap-4 px-5 py-4">
                            <div
                                class="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center transition-all duration-150"
                                :style="isSelected(prop) ? { backgroundColor: 'var(--cp)', color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#6b7280' }"
                            >
                                <i :class="prop.slot === 'morning' ? 'fas fa-sun' : 'fas fa-cloud-sun'" class="text-lg"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="font-bold text-gray-900 text-base capitalize">{{ formatDate(prop.date) }}</p>
                                <p class="text-sm mt-0.5" :style="isSelected(prop) ? { color: 'var(--cp)' } : { color: '#6b7280' }">
                                    {{ slotLabel(prop.slot) }}
                                </p>
                            </div>
                            <div
                                class="w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-150"
                                :style="isSelected(prop) ? { borderColor: 'var(--cp)', backgroundColor: 'var(--cp)' } : { borderColor: '#d1d5db', backgroundColor: '#fff' }"
                            >
                                <i v-if="isSelected(prop)" class="fas fa-check text-white text-xs"></i>
                            </div>
                        </div>
                    </button>

                    <!-- Aucun créneau -->
                    <button
                        @click="selectedSlot = null; noneOfTheAbove = !noneOfTheAbove"
                        class="w-full text-left rounded-2xl border-2 transition-all duration-150 overflow-hidden"
                        :class="noneOfTheAbove ? 'shadow-md' : 'border-gray-150 bg-white hover:border-gray-300 shadow-sm hover:shadow'"
                        :style="noneOfTheAbove ? { borderColor: 'var(--cp)', backgroundColor: 'var(--cp-light)' } : {}"
                    >
                        <div class="flex items-center gap-4 px-5 py-4">
                            <div
                                class="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center transition-all duration-150"
                                :style="noneOfTheAbove ? { backgroundColor: 'var(--cp)', color: '#fff' } : { backgroundColor: '#f3f4f6', color: '#6b7280' }"
                            >
                                <i class="fas fa-calendar-xmark text-lg"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="font-bold text-gray-900 text-base">Aucun créneau ne me convient</p>
                                <p class="text-sm mt-0.5" :style="noneOfTheAbove ? { color: 'var(--cp)' } : { color: '#6b7280' }">
                                    Indiquez vos disponibilités
                                </p>
                            </div>
                            <div
                                class="w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-150"
                                :style="noneOfTheAbove ? { borderColor: 'var(--cp)', backgroundColor: 'var(--cp)' } : { borderColor: '#d1d5db', backgroundColor: '#fff' }"
                            >
                                <i v-if="noneOfTheAbove" class="fas fa-check text-white text-xs"></i>
                            </div>
                        </div>
                    </button>
                </div>

                <!-- Textarea "aucun créneau" -->
                <div v-if="noneOfTheAbove" class="rounded-2xl border border-gray-200 bg-white p-4 mb-3">
                    <label class="text-xs font-semibold text-gray-500 uppercase tracking-widest block mb-2">
                        Vos disponibilités
                    </label>
                    <textarea
                        v-model="noneMessage"
                        rows="3"
                        placeholder="Ex : je suis disponible les matins à partir du 10 mai…"
                        class="w-full text-sm text-gray-800 placeholder-gray-400 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 resize-none focus:outline-none focus:ring-2"
                        :style="{ '--tw-ring-color': 'var(--cp)' }"
                    ></textarea>
                </div>

                <!-- CTA -->
                <button
                    @click="confirmSlot"
                    :disabled="(!selectedSlot && !noneOfTheAbove) || loadingConfirm"
                    class="w-full py-4 rounded-2xl font-bold text-white text-base transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed mt-5"
                    :style="{ backgroundColor: 'var(--cp)' }"
                >
                    <template v-if="loadingConfirm">
                        <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Confirmation en cours…
                    </template>
                    <template v-else>
                        <i class="fas fa-check"></i>
                        {{ noneOfTheAbove ? 'Envoyer mes disponibilités' : selectedSlot ? 'Confirmer ce créneau' : 'Sélectionnez un créneau' }}
                    </template>
                </button>

                <!-- Powered by -->
                <p v-if="config.branding_powered_by" class="text-center text-xs text-gray-300 mt-8">
                    Propulsé par <a href="https://termoflow.fr" target="_blank" class="font-medium text-gray-400 hover:underline">Termoflow</a>
                </p>

            </main>
        </template>
    </div>
</template>

<script>
export default {
    name: 'ClientReservationChooseSlot',
    data() {
        return {
            reservation:   null,
            alertMessage:  null,
            selectedSlot:  null,
            noneOfTheAbove: false,
            noneMessage:   '',
            confirmed:     false,

            loadingGetReservation: false,
            loadingConfirm:        false,
        }
    },
    computed: {
        config() {
            return this.reservation?.config || {};
        },
        companyName() {
            return this.config.branding_company_name || '';
        },
        propositions() {
            if (!this.reservation?.propositions) return [];
            try {
                return typeof this.reservation.propositions === 'string'
                    ? JSON.parse(this.reservation.propositions)
                    : this.reservation.propositions;
            } catch {
                return [];
            }
        },
        appareilLabel() {
            return this.reservation?.appareil_type?.replace(/_/g, ' ') || '';
        },
        cssVars() {
            const c = this.config;
            return {
                '--cp':       c.color_primary       || '#347f2f',
                '--cp-light': c.color_primary_light  || '#edf7ed',
                '--cp-dark':  c.color_primary_dark   || '#1f5c1a',
            };
        },
    },
    methods: {
        async getReservation() {
            try {
                this.loadingGetReservation = true;
                const response = await this.$request.get(`/reservations/token/${this.$route.params.reservationToken}`);
                this.reservation = response;

                if (!this.reservation) {
                    this.alertMessage = "Réservation introuvable. Veuillez vérifier le lien ou contacter l'entreprise.";
                } else if (this.reservation.statut !== 'propositions_envoyees') {
                    this.alertMessage = "Ce lien n'est plus actif. Le créneau a peut-être déjà été confirmé.";
                }

                // Si un seul créneau est proposé, on le sélectionne par défaut pour simplifier le parcours
                if (this.propositions.length === 1) {
                    this.selectedSlot = this.propositions[0];
                }
            } catch {
                this.alertMessage = "Impossible de charger la page. Veuillez réessayer plus tard.";
            } finally {
                this.loadingGetReservation = false;
            }
        },

        async confirmSlot() {
            if (!this.selectedSlot && !this.noneOfTheAbove) return;
            try {
                this.loadingConfirm = true;

                if (this.noneOfTheAbove) {
                    await this.$request.post(`/reservations/token/${this.$route.params.reservationToken}/choose-slot`, {
                        slot:              null,
                        none_of_the_above: true,
                        message:           this.noneMessage,
                    });
                } else {
                    await this.$request.post('/interventions/create-from-json', {
                        client: {
                            nom:    this.reservation.client_nom,
                            prenom: this.reservation.client_prenom,
                            email:  this.reservation.client_email,
                            tel:    this.reservation.client_telephone,
                        },
                        adresse: {
                            adresse1:    this.reservation.adresse1,
                            code_postal: this.reservation.code_postal,
                            ville:       this.reservation.ville,
                            latitude:    this.reservation.latitude,
                            longitude:   this.reservation.longitude,
                        },
                        intervention_type_id: this.reservation.intervention_type_id,
                        date_intervention:    this.selectedSlot.date,
                        moment_day:           this.selectedSlot.slot,
                        technicien_id: this.selectedSlot.tech_id || null,
                    });

                    await this.$request.post(`/reservations/token/${this.$route.params.reservationToken}/choose-slot`, {
                        slot: this.selectedSlot,
                    });
                }

                this.confirmed = true;
            } catch {
                this.$toast.error("Une erreur est survenue. Veuillez réessayer.");
            } finally {
                this.loadingConfirm = false;
            }
        },

        isSelected(prop) {
            if (!this.selectedSlot) return false;
            return this.selectedSlot.date === prop.date && this.selectedSlot.slot === prop.slot;
        },

        formatDate(dateStr) {
            return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            });
        },

        slotLabel(slot) {
            return slot === 'morning' ? 'Demi-journée du matin' : "Demi-journée de l'après-midi";
        },
    },
    mounted() {
        this.getReservation();
    },
}
</script>